import { useMemo, useState, type ReactNode } from "react";
import { useLang } from "../i18n";
import { Icon } from "./Icon";
import type { IconName } from "./icons";
import { RowMenu, type MenuItem } from "./RowMenu";

// The prototype's list card (erp-core.js listView): quick search, Custom search panel with chips, refresh,
// density, CSV export, pager and a ⋮ menu per row. Custom search values go to the server as query
// parameters (the caller loads with them); the quick search filters the loaded rows, as in the prototype.

export interface Column<R> { k: string; label: string; cell(r: R): ReactNode; csv(r: R): string | number; end?: boolean }
export interface SearchField { k: string; label: string; type: "text" | "select"; options?: [string, string][]; disabled?: boolean; hint?: string }

export function ListView<R extends { id: string }>({ rows, error, columns, search, filters, onFilters, onRefresh, quick, actions, newButton, tools, above, empty, file, testId }: {
  rows: R[] | null; error: string | null; columns: Column<R>[];
  search?: SearchField[]; filters?: Record<string, string>; onFilters?(f: Record<string, string>): void;
  onRefresh(): void; quick(r: R): string; actions(r: R): MenuItem[]; newButton?: ReactNode;
  /** Extra toolbar buttons before the New button, and a line shown above the rows (prototype extraTools, above). */
  tools?: ReactNode; above?: ReactNode;
  empty: { icon: IconName; title: string; body: string }; file: string; testId?: string;
}) {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [dense, setDense] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const f = filters ?? {};
  const active = Object.entries(f).filter(([, v]) => v);

  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (rows ?? []).filter((r) => !term || quick(r).toLowerCase().includes(term));
  }, [rows, q, quick]);
  const pages = Math.max(1, Math.ceil(shown.length / size));
  const cur = Math.min(page, pages);
  const slice = shown.slice((cur - 1) * size, cur * size);

  const apply = (x: Record<string, string>) => { onFilters?.(x); setPage(1); };
  const chipValue = (sf: SearchField, v: string) => sf.options?.find(([a]) => a === v)?.[1] ?? v;
  function exportCsv() {
    const lines = [columns.map((c) => c.label), ...shown.map((r) => columns.map((c) => c.csv(r)))];
    const text = "﻿" + lines.map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
    a.download = `${file}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  }

  const chips = active.length || q ? (
    <div className="chips">
      {active.map(([k, v]) => { const sf = search!.find((x) => x.k === k)!; return (
        <span key={k} className="fchip">{sf.label}: <b>{chipValue(sf, v)}</b><button onClick={() => apply({ ...f, [k]: "" })} aria-label={t("removeFilter")}><Icon name="x" /></button></span>
      ); })}
      {q && <span className="fchip">{t("searchWord")}: <b>{q}</b><button onClick={() => setQ("")} aria-label={t("removeFilter")}><Icon name="x" /></button></span>}
      <button className="btn btn-sm btn-ghost" onClick={() => { setQ(""); apply({}); }}>{t("clearAll")}</button>
    </div>
  ) : null;

  let region: ReactNode;
  if (error) region = <div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{error}</p><button className="btn btn-primary" onClick={onRefresh}><Icon name="refresh" />{t("retry")}</button></div>;
  else if (!rows) region = <div className="state-box"><p>{t("loading")}</p></div>;
  else if (!rows.length && !active.length) region = <div className="state-box"><Icon name={empty.icon} /><h3>{empty.title}</h3><p>{empty.body}</p>{newButton}</div>;
  else if (!shown.length) region = <>{chips}<div className="state-box"><Icon name="search" /><h3>{t("noResults")}</h3><p>{t("noResultsBody")}</p><button className="btn" onClick={() => { setQ(""); apply({}); }}>{t("clearAll")}</button></div></>;
  else region = (
    <>
      {chips}
      <div className="table-wrap"><table className={`tbl list-tbl ${dense ? "dense" : ""}`} data-testid={testId}>
        <thead><tr><th className="w-idx">#</th>{columns.map((c) => <th key={c.k} className={c.end ? "end" : ""}>{c.label}</th>)}<th className="end">{t("options")}</th></tr></thead>
        <tbody>{slice.map((r, i) => (
          <tr key={r.id}>
            <td className="w-idx num muted">{(cur - 1) * size + i + 1}</td>
            {columns.map((c) => <td key={c.k} className={c.end ? "end num" : ""}>{c.cell(r)}</td>)}
            <td className="end"><RowMenu label={`${t("options")} · ${quick(r)}`} items={actions(r)} /></td>
          </tr>
        ))}</tbody>
      </table></div>
      <div className="pager">
        <span>{t("showingOf", { a: (cur - 1) * size + 1, b: (cur - 1) * size + slice.length, n: shown.length })}</span><span className="spacer" />
        <label className="pg-size">{t("rowsPerPage")} <select className="control" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}>{[10, 25, 50].map((n) => <option key={n}>{n}</option>)}</select></label>
        <button className="btn btn-sm btn-icon" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label={t("prevPage")}><Icon name="chevR" className="rev" /></button>
        <span className="num">{cur} / {pages}</span>
        <button className="btn btn-sm btn-icon" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label={t("nextPage")}><Icon name="chevR" className="flip" /></button>
      </div>
    </>
  );

  return (
    <section className="card list-card">
      <div className="ltb">
        <div className="input-group lq"><span className="lead"><Icon name="search" /></span><input className="control" type="search" placeholder={t("searchWord")} aria-label={t("searchWord")} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} /></div>
        <span className="spacer" />
        <button className="btn btn-sm btn-ghost btn-icon" onClick={onRefresh} title={t("refreshResults")} aria-label={t("refreshResults")}><Icon name="refresh" /></button>
        {search && (
          <button className={`btn btn-sm ${open ? "on" : ""}`} aria-expanded={open} onClick={() => { setOpen(!open); setDraft(f); }}>
            <Icon name="filter" />{t("search")}{active.length ? <> <span className="count num">{active.length}</span></> : null}
          </button>
        )}
        <button className="btn btn-sm btn-ghost btn-icon" onClick={() => setDense(!dense)} title={t("density")} aria-label={t("density")} aria-pressed={dense}><Icon name="density" /></button>
        <button className="btn btn-sm" onClick={exportCsv} disabled={!shown.length}><Icon name="download" /><span className="hide-sm">{t("export")}</span></button>
        {tools}{newButton}
      </div>
      {search && (
        <div className={`lsearch ${open ? "open" : ""}`} data-testid="custom-search">
          <div className="ls-head"><b><Icon name="filter" />{t("customSearch")}</b></div>
          <div className="ls-grid">{search.map((sf) => (
            <label key={sf.k} className="field" title={sf.hint}>
              <span className="label">{sf.label}</span>
              {sf.type === "select"
                ? <select className="control" value={draft[sf.k] ?? ""} disabled={sf.disabled} onChange={(e) => setDraft({ ...draft, [sf.k]: e.target.value })}>
                    <option value="">{t("all")}</option>{sf.options?.map(([a, b]) => <option key={a} value={a}>{b}</option>)}
                  </select>
                : <input className="control" style={{ textAlign: "start" }} value={draft[sf.k] ?? ""} disabled={sf.disabled} onChange={(e) => setDraft({ ...draft, [sf.k]: e.target.value })} />}
              {sf.hint && <span className="hint">{sf.hint}</span>}
            </label>
          ))}</div>
          <div className="ls-foot">
            <button className="btn btn-sm" onClick={() => { setDraft({}); apply({}); }}>{t("reset")}</button>
            <button className="btn btn-sm btn-primary" onClick={() => apply(draft)}><Icon name="search" />{t("search")}</button>
          </div>
        </div>
      )}
      {above}
      {region}
    </section>
  );
}

/** A field whose module is not built yet: shown in place, locked, with the dependency named (tracked as partial). */
export function Pending({ label, module, full, note }: { label: string; module: string; full?: boolean; note?: string }) {
  const { t } = useLang();
  return (
    <div className={`field ${full ? "full" : ""} pending-dep`} data-testid="pending-field">
      <label>{label}</label>
      <div className="control dep-lock" aria-disabled="true"><Icon name="lock" /><span>{note ?? t("waitsFor", { m: module })}</span></div>
    </div>
  );
}
